import React from 'react';

export type ToggleTone = 'secondary' | 'primary';
export type ToggleSize = 'sm' | 'md';

export interface ToggleProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  /** Active pressed state */
  pressed: boolean;
  /** Callback invoked when the toggle state changes */
  onPressedChange?: (pressed: boolean) => void;
  /** Active highlight tone: 'secondary' (green/loop/practice) or 'primary' (blue) */
  tone?: ToggleTone;
  /** Control size: sm (32px), md (40px) with 44px minimum touch targets on mobile */
  size?: ToggleSize;
  /** Icon placed at inline-start (right side in Persian RTL) */
  icon?: React.ReactNode;
  /** Convenience alias for icon */
  leadingIcon?: React.ReactNode;
}

export const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      pressed,
      onPressedChange,
      tone = 'secondary',
      size = 'md',
      icon,
      leadingIcon,
      children,
      className = '',
      onClick,
      disabled,
      ...props
    },
    ref
  ) => {
    const lead = leadingIcon ?? icon;

    const sizeClasses: Record<ToggleSize, string> = {
      sm: 'min-h-[44px] md:min-h-9 h-11 md:h-9 px-2.5 text-xs md:text-[13px] rounded-[10px]',
      md: 'min-h-[44px] md:min-h-10 h-11 md:h-10 px-3.5 text-sm rounded-xl'
    };

    const toneActiveClasses: Record<ToggleTone, string> = {
      secondary: 'bg-secondary/12 border border-secondary/40 text-secondary-soft hover:bg-secondary/20',
      primary: 'bg-primary/14 border border-primary text-primary-soft hover:bg-primary/25'
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) {
        onPressedChange?.(!pressed);
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        role="button"
        aria-pressed={pressed}
        data-state={pressed ? 'on' : 'off'}
        disabled={disabled}
        onClick={handleClick}
        className={`inline-flex items-center justify-center gap-2 font-semibold border select-none transition-all duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 ring-primary ring-offset-2 ring-offset-base-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none focus-ring ${
          sizeClasses[size]
        } ${
          pressed
            ? toneActiveClasses[tone]
            : 'bg-neutral border-base-300 text-neutral-content/80 hover:text-base-content hover:bg-base-300/80 hover:border-base-content/30'
        } ${className}`}
        {...props}
      >
        {lead}
        {children && <span>{children}</span>}
      </button>
    );
  }
);

Toggle.displayName = 'Toggle';
